import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  addServiceWatch,
  closeSystemAlarm,
  endSystemMaintenance,
  removeServiceWatch,
  setAlarmTypeWatch,
  startSystemMaintenance,
  systemAlarmsPageQuery,
  type DeviceSystemAlarmsPage,
  type SystemAlarmRow,
} from '../api/devices'
import { FormActions, FormRow, HalfField } from '../ui/FormGrid'
import { toast } from '../ui/notify'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'

/**
 * Fila System Alarms - proactivitee/devices/details/deviceAlarms.php plus
 * alarmsList.php. Tabla e CoverageBoard (AlarmCoverage::forDevice). Enable /
 * Disable posteaza type+enabled ca setAlarmTypeWatch; Watch service/port
 * posteaza kind/name/port. Close, Start si End maintenance merg tot la
 * /api/v2/devices/{id}/system-alarms/... - portalul CI3 nu mai exista pe
 * server, deci un <form action="/proactivitee/..."> nu mai are unde ajunge.
 *
 * -----------------------------------------------------------------------------
 * CE E FORMULAR AICI SI CE NU
 * -----------------------------------------------------------------------------
 *
 * Aproape tot panoul e TABLA: casutele de sus, tabla de acoperire, lista
 * alarmelor deschise, istoricul si pastilele lui de filtru. Alea nu trec pe
 * grila din `ui/FormGrid` si nici n-ar avea ce cauta acolo - un tabel isi are
 * latimile lui, si o citire cu eticheta langa valoare spune mai mult decat una
 * cu eticheta deasupra.
 *
 * Doua lucruri SUNT formular, si atat s-a schimbat:
 *
 *   - randurile de adaugat (fereastra de mentenanta, serviciul urmarit, portul
 *     urmarit) sunt BARE de unelte - alegere, nume, buton, toate pe un rand -
 *     deci raman bare de unelte. Ce nu era in regula la ele era ca purtau
 *     casute GOALE: nici `form-control`, nici altceva, adica exact widgetul
 *     desenat de sistemul de operare, langa butoane Bootstrap. `od_console.css`
 *     le-ar fi imbracat singura (`body.od-console input, select, textarea`,
 *     randul 112), dar clasa `od-console` se pune numai pe zonele Settings si
 *     feedback, nu si pe fisa unei masini - vezi `Shell.tsx`. Deci si-au primit
 *     `form-control`, care e imbracat de `app.css` oriunde;
 *   - caseta de la Close, care e un formular de un camp, a trecut pe
 *     `FormRow` / `HalfField` / `FormActions`.
 *
 * Numele campurilor NU s-au atins. `startSystemMaintenance` isi citeste cele
 * patru valori prin `form.elements.namedItem(...)`, deci un `name` schimbat ar
 * rupe exact legatura care s-a reparat pe 10 septembrie 2026, cand cele doua
 * jumatati vorbeau cu nume diferite.
 */

const SEVERITIES: Array<{ value: string; label: string; tone: string; icon: string; more: string }> = [
  { value: 'all', label: 'All', tone: 'blue-hoki', icon: 'fa-layer-group', more: 'Open now' },
  { value: '3', label: 'Critical', tone: 'red-intense', icon: 'fa-exclamation-circle', more: 'Open critical' },
  { value: '2', label: 'Major', tone: 'yellow', icon: 'fa-exclamation-triangle', more: 'Open major' },
  { value: '1', label: 'Minor', tone: 'green-haze', icon: 'fa-info-circle', more: 'Open minor' },
  { value: '0', label: 'Info', tone: 'blue-madison', icon: 'fa-info', more: 'All monitored · values now' },
]

const STATE_TONE: Record<string, string> = {
  critical: '#e34c4c',
  silent: '#8a94a6',
  major: '#f0ad4e',
  minor: '#5cb85c',
  forecast: '#7c6fcd',
  info: '#5b9bd5',
  ok: '#37addb',
  off: '#b8c2cf',
}

const HIST_TONE: Record<number, string> = { 3: '#e34c4c', 2: '#f0ad4e', 1: '#5cb85c' }

export function DeviceSystemAlarms({ id }: ScreenProps) {
  const page = useQuery(systemAlarmsPageQuery(id))
  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />
  return <Board id={id} page={page.data} />
}

function Board({ id, page }: { id: number; page: DeviceSystemAlarmsPage }) {
  const query = useQueryClient()
  const [tile, setTile] = useState('all')
  const [histQ, setHistQ] = useState('')
  const [histSev, setHistSev] = useState('')
  const [histAge, setHistAge] = useState('')
  const [histBy, setHistBy] = useState('')
  const [closeId, setCloseId] = useState<number | null>(null)

  const layer = page.coverage.layer
  const rows = page.coverage.rows
  const monitored = rows.filter((r) => r.watched).length
  const openTotal = Object.values(page.alarm_counts).reduce((a, b) => a + b, 0)
  const info = tile === '0'
  const refresh = () => query.invalidateQueries({ queryKey: ['device', id, 'system-alarms'] })

  const openShown = useMemo(() => {
    if (tile === 'all' || tile === '0') return page.open_alarms
    return page.open_alarms.filter((a) => String(a.severity) === tile)
  }, [page.open_alarms, tile])

  const hist = useMemo(() => {
    const now = Math.floor(Date.now() / 1000)
    const q = histQ.trim().toLowerCase()
    return page.alarm_history.filter((past) => {
      if (histSev !== '' && String(past.severity) !== histSev) return false
      const closed = past.closed_unix || 0
      if (histAge !== '' && now - closed > Number(histAge)) return false
      const by = past.closed_by || (past.closed_by_name ? 'person' : 'agent')
      if (histBy !== '' && by !== histBy) return false
      if (q !== '') {
        const hay = [
          past.type_name,
          past.hardware_identifier || '',
          past.note,
          past.value_string,
          past.closed_by_name || 'agent',
        ]
          .join(' ')
          .toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
  }, [page.alarm_history, histQ, histSev, histAge, histBy])

  return (
    <div className="device-system-alarms">
      <style>{BOARD_CSS}</style>
      <LayerBanner page={page} layer={layer} />

      <div className="sa-section-title">Right now</div>
      <div className="row sa-tiles">
        {SEVERITIES.map((meta) => {
          const count = meta.value === 'all' ? openTotal : meta.value === '0' ? monitored : page.alarm_counts[meta.value] || 0
          return (
            <div
              key={meta.value}
              className={`col-md-2 sa-tile${tile === meta.value ? ' sa-active' : ''}`}
              data-severity={meta.value}
              data-device={id}
              onClick={() => setTile(meta.value)}
            >
              <div className={`dashboard-stat ${meta.tone}`}>
                <div className="visual">
                  <i className={`fas ${meta.icon}`}></i>
                </div>
                <div className="details">
                  <div className="number">{count}</div>
                  <div className="desc">{meta.label}</div>
                </div>
                <div className="more">{meta.more}</div>
                <span className="sa-chev">
                  <i className="fas fa-chevron-down" aria-hidden="true"></i>
                </span>
              </div>
            </div>
          )
        })}
      </div>

      <div className={`row sa-split${info ? ' is-wide-now' : ''}`} id="sa-split">
        <div className="col-md-7 sa-col-now" id="sa-col-now">
          <div className="sa-section-title" style={{ marginTop: 0 }}>
            Now
          </div>
          <div className="sa-pane" id="sa-board-pane" hidden={!info}>
            <CoverageTable id={id} page={page} rows={rows} onToggled={refresh} />
            <Watches id={id} page={page} onChanged={refresh} />
          </div>
          <div className="sa-pane" id="sa-open-pane" hidden={info}>
            <div className="row">
              <div className="col-md-12 alarmsContainer">
                <OpenList
                  id={id}
                  alarms={tile === '0' ? page.open_alarms : openShown}
                  closeId={closeId}
                  onClose={setCloseId}
                  onClosed={refresh}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-5 sa-col-history" id="sa-col-history">
          <div className="sa-section-title" style={{ marginTop: 0 }}>
            History{' '}
            <span className="sa-hist-count" id="sa-hist-count">
              {page.alarm_history.length
                ? hist.length === page.alarm_history.length
                  ? page.alarm_history.length
                  : `${hist.length} of ${page.alarm_history.length}`
                : ''}
            </span>
          </div>
          <div className="sa-hist-tools" id="sa-hist-tools">
            <input
              type="search"
              id="sa-hist-search"
              placeholder="Search history: signal, disk, service, who closed it…"
              autoComplete="off"
              value={histQ}
              onChange={(e) => setHistQ(e.target.value)}
            />
            <Chips
              filter="sev"
              value={histSev}
              onChange={setHistSev}
              items={[
                { v: '', label: 'All' },
                { v: '3', label: 'Critical', dot: '#e34c4c' },
                { v: '2', label: 'Major', dot: '#f0ad4e' },
                { v: '1', label: 'Minor', dot: '#5cb85c' },
                { v: '0', label: 'Info', dot: '#5b9bd5' },
              ]}
            />
            <Chips
              filter="age"
              value={histAge}
              onChange={setHistAge}
              items={[
                { v: '86400', label: '24 h' },
                { v: '604800', label: '7 days' },
                { v: '2592000', label: '30 days' },
                { v: '', label: 'Everything' },
              ]}
            />
            <Chips
              filter="by"
              value={histBy}
              onChange={setHistBy}
              items={[
                { v: '', label: 'Closed by anyone' },
                { v: 'agent', label: 'by the agent' },
                { v: 'person', label: 'by a person' },
              ]}
            />
          </div>
          <HistoryList all={page.alarm_history} shown={hist} />
        </div>
      </div>
    </div>
  )
}

function LayerBanner({
  page,
  layer,
}: {
  page: DeviceSystemAlarmsPage
  layer: { label: string; watching: boolean }
}) {
  const off = page.coverage_error || !layer.watching
  return (
    <div className={`sa-layer${off ? ' off' : ''}`}>
      {page.coverage_error ? (
        <>
          <strong>The board could not be read.</strong>
          <div className="sa-muted" style={{ marginTop: 4 }}>
            {page.coverage_problems.length > 0 && <>Could not read {sentence(page.coverage_problems)}. </>}
            Open and recently closed alarms below are still from this machine.
            {page.global_admin && <ProblemWhy details={page.coverage_problem_details} />}
          </div>
        </>
      ) : (
        <>
          <strong>Settings in force:</strong> {layer.label}
          {!layer.watching && (
            <div className="sa-muted" style={{ marginTop: 4 }}>
              This machine is not creating alarms until proactive support is turned on.
            </div>
          )}
          {page.coverage_problems.length > 0 && (
            <div className="sa-muted" style={{ marginTop: 4 }}>
              Some of it is missing: could not read {sentence(page.coverage_problems)}. What is shown below is the rest.
              {page.global_admin && <ProblemWhy details={page.coverage_problem_details} />}
            </div>
          )}
        </>
      )}
    </div>
  )
}

function ProblemWhy({ details }: { details: Array<{ step: string; what: string }> }) {
  if (!details.length) return null
  return (
    <div className="sa-muted" style={{ marginTop: 6, fontSize: 11 }}>
      {details.map((p) =>
        p.step === '' ? null : (
          <div key={p.step}>
            <code>{p.step}</code>
            {p.what !== '' && <> &mdash; {p.what}</>}
          </div>
        ),
      )}
    </div>
  )
}

function CoverageTable({
  id,
  page,
  rows,
  onToggled,
}: {
  id: number
  page: DeviceSystemAlarmsPage
  rows: SystemAlarmRow[]
  onToggled(): Promise<void>
}) {
  const silent = page.coverage.silent
  return (
    <>
      <p className="sa-muted" style={{ margin: '0 0 12px' }}>
        Everything worked out for this machine: which module owns the signal, what it reads right now, the alarm state,
        the threshold and where it comes from, and Enable / Disable for this box only.
      </p>
      <div className="row">
        <div className="col-md-12">
          {silent !== null && !page.coverage_error && rows.length > 0 && (
            <p
              className="sa-muted"
              style={{ margin: '0 0 10px', padding: '8px 10px', borderLeft: '3px solid #8a94a6', background: 'rgba(138,148,166,.08)' }}
            >
              <i className="fas fa-satellite-dish"></i> <strong>Silent.</strong> The agent has not reported for{' '}
              {silentMinutes(silent)}. The states below are what it last said, not what is true now. A grey dot means
              unknown, not OK.
            </p>
          )}
          {page.coverage_error ? (
            <p className="color-light-grey" style={{ fontSize: 12 }}>
              <i className="fas fa-info-circle"></i> Signals are missing because the board could not be read, not
              because this machine has none.
            </p>
          ) : rows.length === 0 ? (
            <p className="color-light-grey" style={{ fontSize: 12 }}>
              <i className="fas fa-info-circle"></i> No signals to show on this machine yet.
            </p>
          ) : (
            <table className="table table-hover sa-board is-wide is-all">
              <thead>
                <tr>
                  <th></th>
                  <th>Signal</th>
                  <th className="sa-col-module">Module</th>
                  <th>Now</th>
                  <th>State</th>
                  <th>
                    <span className="sa-watch-now">Enable</span>
                    <span className="sa-watch-else">Included</span>
                  </th>
                  <th className="sa-col-layer">Applies from</th>
                  <th className="sa-col-threshold">Threshold</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const dot = STATE_TONE[row.state] || '#b8c2cf'
                  const href = row.href ? `${row.href}${id}` : ''
                  const toggleable = page.can_edit_alarms && !!row.toggleable
                  return (
                    <tr key={`${row.type}-${i}`} data-home={row.board || row.home}>
                      <td className="sa-dot">
                        <span style={{ background: dot }}></span>
                      </td>
                      <td>
                        {href ? (
                          <a href={href} className="action_button">
                            {row.signal}
                          </a>
                        ) : (
                          <strong>{row.signal}</strong>
                        )}
                        {row.culprit ? <div className="sa-culprit">Culprit: {row.culprit}</div> : null}
                        {row.value_string ? <div className="sa-culprit">{row.value_string}</div> : null}
                      </td>
                      <td className="sa-col-module sa-module-cell">{row.home_module}</td>
                      <td className="sa-current">
                        {row.current}
                        {row.forecast?.label ? (
                          <div className="sa-muted">
                            {row.forecast.label}
                            {row.forecast.rate ? <> · {row.forecast.rate}</> : null}
                          </div>
                        ) : null}
                      </td>
                      <td>{row.state_label}</td>
                      <td>
                        <span className="sa-include-text">{row.watched ? 'On' : 'Off'}</span>
                        {toggleable && (
                          <button
                            type="button"
                            className="btn btn-default btn-sm sa-toggle"
                            data-type={row.type}
                            data-on={row.watched ? '1' : '0'}
                            onClick={() => {
                              void setAlarmTypeWatch(id, row.type, !row.watched)
                                .then(() => onToggled())
                                .catch((err: Error) => toast(err.message || 'The signal could not be updated.', 'Error', 'error'))
                            }}
                          >
                            {row.watched ? 'Disable' : 'Enable'}
                          </button>
                        )}
                      </td>
                      <td className="sa-muted sa-col-layer">{row.layer}</td>
                      <td className="sa-col-threshold">{row.threshold}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  )
}

function Watches({
  id,
  page,
  onChanged,
}: {
  id: number
  page: DeviceSystemAlarmsPage
  onChanged(): Promise<void>
}) {
  const [svc, setSvc] = useState('')
  const [svcCustom, setSvcCustom] = useState('')
  const [port, setPort] = useState('')
  const [portCustom, setPortCustom] = useState('')
  const [portLabel, setPortLabel] = useState('')
  const quiet = page.quiet_window
  const services = page.watch_candidates.services
  const ports = page.watch_candidates.ports

  return (
    <div className="sa-watches" id="sa-watches" data-device={id}>
      <h4>Maintenance</h4>
      {quiet ? (
        <p
          className="sa-muted sa-quiet-now"
          style={{ margin: '0 0 10px', padding: '8px 10px', borderLeft: '3px solid #9e6b00', background: 'rgba(158,107,0,.08)' }}
        >
          <i className="fas fa-moon"></i> <strong>Quiet now: {quiet.name}.</strong> {quiet.describe}
          {quiet.scope_type === 3 ? '' : ` · set for the ${quiet.scope_type === 2 ? 'alarm group' : 'whole company'}`}
          {quiet.scope_type === 3 && (
            <form
              method="post"
              style={{ display: 'inline' }}
              onSubmit={(e) => {
                e.preventDefault()
                void endSystemMaintenance(id, quiet.id)
                  .then(() => onChanged())
                  .catch(() => toast('The maintenance window could not be ended.', 'Error', 'error'))
              }}
            >
              <input type="hidden" name="id" value={quiet.id} />
              <button type="submit" className="btn btn-default btn-xs" id="sa-quiet-end" data-id={quiet.id} style={{ marginLeft: 8 }}>
                End now
              </button>
            </form>
          )}
        </p>
      ) : (
        <>
          <p className="sa-watch-help">
            Going to reboot, patch or move this machine? Start a maintenance window: alarms that open meanwhile are kept
            quiet - no mail, no ticket, no bell, no AI - and a digest tells you what happened when it ends. Recurring
            quiet hours for the whole company live in Settings › Proactivitee › Quiet hours.
          </p>
          <form
            className="sa-watch-add"
            data-kind="quiet"
            id="sa-quiet-form"
            method="post"
            onSubmit={(e) => {
              e.preventDefault()
              const form = e.currentTarget
              const field = (name: string): string =>
                (form.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement | null)?.value ?? ''
              void startSystemMaintenance(id, {
                hours: field('hours'),
                mode: field('mode'),
                pass: field('pass'),
                name: field('name'),
              })
                .then(() => onChanged())
                .catch(() => toast('The maintenance window could not be started.', 'Error', 'error'))
            }}
          >
            <select id="sa-quiet-hours" className="form-control" name="hours" defaultValue="2">
              <option value="1">1 hour</option>
              <option value="2">2 hours</option>
              <option value="4">4 hours</option>
              <option value="8">8 hours</option>
              <option value="24">24 hours</option>
              <option value="72">3 days</option>
            </select>
            <select id="sa-quiet-mode" className="form-control" name="mode" defaultValue="1">
              <option value="1">Hush: alarms open, nobody is told</option>
              <option value="2">Silence: alarms are not opened</option>
            </select>
            <select id="sa-quiet-pass" className="form-control" name="pass" defaultValue="3">
              <option value="3">Critical still gets through</option>
              <option value="2">Major and Critical get through</option>
              <option value="0">Nothing gets through</option>
            </select>
            <input type="text" id="sa-quiet-name" className="form-control" name="name" placeholder="reason (optional)" maxLength={120} style={{ maxWidth: 200 }} />
            <button type="submit" className="btn btn-default btn-sm" id="sa-quiet-start">
              <i className="fas fa-moon"></i> Start maintenance
            </button>
          </form>
        </>
      )}

      <h4 style={{ marginTop: 18 }}>On this machine</h4>
      <p className="sa-watch-help">
        Live alarms (CPU, disk, connections) belong to the company. These watches belong to this box: a named service
        that must stay running, or a TCP port that must keep listening.
      </p>
      {page.watches.length === 0 ? (
        <p className="sa-muted" style={{ margin: '0 0 10px' }}>
          Nothing watched yet on this machine.
        </p>
      ) : (
        <ul className="sa-watch-list">
          {page.watches.map((watch) => {
            const kind = watch.kind === 'port' ? 'port' : 'service'
            const label =
              kind === 'port' ? (watch.name !== '' ? `${watch.name} · ${watch.port}` : `:${watch.port}`) : watch.name
            return (
              <li key={`${kind}-${watch.name}-${watch.port}`}>
                <span className="sa-watch-kind">{kind === 'port' ? 'Port' : 'Service'}</span>
                <span>{label}</span>
                <button
                  type="button"
                  className="sa-watch-remove"
                  data-kind={kind}
                  data-name={watch.name}
                  data-port={watch.port}
                  onClick={() => {
                    void removeServiceWatch(id, kind, watch.name, watch.port)
                      .then(() => onChanged())
                      .catch(() => toast('The watch could not be removed.', 'Error', 'error'))
                  }}
                >
                  Remove
                </button>
              </li>
            )
          })}
        </ul>
      )}

      <div className="sa-watch-add" data-kind="service">
        <select id="sa-watch-service" className="form-control" value={svc} onChange={(e) => setSvc(e.target.value)}>
          <option value="">Service on this machine…</option>
          {services.map((s) => (
            <option key={s.name} value={s.name}>
              {s.label}
            </option>
          ))}
        </select>
        <input
          type="text"
          id="sa-watch-service-custom"
          className="form-control"
          placeholder="or type the unit name"
          maxLength={190}
          value={svcCustom}
          onChange={(e) => setSvcCustom(e.target.value)}
        />
        <button
          type="button"
          className="btn btn-default btn-sm"
          id="sa-watch-add-service"
          onClick={() => {
            const name = svc.trim() || svcCustom.trim()
            if (!name) return
            void addServiceWatch(id, 'service', name, 0)
              .then(() => onChanged())
              .catch(() => toast('The watch could not be saved.', 'Error', 'error'))
          }}
        >
          Watch service
        </button>
      </div>
      <div className="sa-watch-add" data-kind="port">
        <select id="sa-watch-port" className="form-control" value={port} onChange={(e) => setPort(e.target.value)}>
          <option value="">Port listening now…</option>
          {ports.map((p) => (
            <option key={p.port} value={p.port} data-name={p.name}>
              {p.label}
            </option>
          ))}
        </select>
        <input
          type="number"
          id="sa-watch-port-custom"
          className="form-control"
          placeholder="or type the port"
          min={1}
          max={65535}
          value={portCustom}
          onChange={(e) => setPortCustom(e.target.value)}
        />
        <input
          type="text"
          id="sa-watch-port-label"
          className="form-control"
          placeholder="optional name"
          maxLength={190}
          style={{ maxWidth: 160 }}
          value={portLabel}
          onChange={(e) => setPortLabel(e.target.value)}
        />
        <button
          type="button"
          className="btn btn-default btn-sm"
          id="sa-watch-add-port"
          onClick={() => {
            const chosen = ports.find((p) => String(p.port) === port)
            let n = parseInt(port || '0', 10)
            let name = chosen?.name || ''
            if (!(n > 0)) {
              n = parseInt(portCustom || '0', 10)
              name = portLabel.trim()
            } else if (portLabel.trim()) {
              name = portLabel.trim()
            }
            if (!(n > 0 && n < 65536)) return
            void addServiceWatch(id, 'port', name, n)
              .then(() => onChanged())
              .catch(() => toast('The watch could not be saved.', 'Error', 'error'))
          }}
        >
          Watch port
        </button>
      </div>
    </div>
  )
}

function OpenList({
  id,
  alarms,
  closeId,
  onClose,
  onClosed,
}: {
  id: number
  alarms: DeviceSystemAlarmsPage['open_alarms']
  closeId: number | null
  onClose(id: number | null): void
  onClosed(): Promise<void>
}) {
  if (alarms.length === 0) {
    return (
      <ul className="feeds">
        <li style={{ background: 'none' }}>
          <div className="alert alert-success">
            <strong>
              <i className="far fa-thumbs-up"></i> Great!
            </strong>{' '}
            No alarms here.
          </div>
        </li>
      </ul>
    )
  }
  return (
    <ul className="feeds">
      {alarms.map((alarm) => {
        const color =
          alarm.severity === 1 ? 'label-success' : alarm.severity === 2 ? 'label-warning' : alarm.severity === 3 ? 'label-danger' : 'label-primary'
        return (
          <li key={alarm.ID}>
            <div className="col1">
              <div className="cont">
                <div className="cont-col1">
                  <div style={{ margin: 0 }} className={`label label-sm ${color}`}>
                    <i className="far fa-bell"></i>
                  </div>
                </div>
                <div className="cont-col2">
                  <div className="desc">
                    {alarm.type_name}{' '}
                    {alarm.dinamic === 1 ? (
                      <label className="label label-danger font-10">
                        {alarm.hardware_identifier} {alarm.value_numeric}
                        {alarm.sign}
                      </label>
                    ) : alarm.hardware_identifier ? (
                      <label className="label label-danger font-10">{alarm.hardware_identifier}</label>
                    ) : null}
                    <em>
                      {' '}
                      on {alarm.created_day} at {alarm.created_time}
                    </em>
                    {alarm.value_string ? (
                      <p style={{ color: '#ff9934' }}>
                        <em>{alarm.value_string}</em>
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
            <div className="col2">
              <div className="feed-actions">
                <button className="metro-btn small danger closeAlarm" data-alarm={alarm.ID} type="button" onClick={() => onClose(alarm.ID)}>
                  Close
                </button>
              </div>
            </div>
            {closeId === alarm.ID && <CloseAlarmForm id={id} alarm={alarm.ID} onDone={onClose} onClosed={onClosed} />}
          </li>
        )
      })}
    </ul>
  )
}

function CloseAlarmForm({
  id,
  alarm,
  onDone,
  onClosed,
}: {
  id: number
  alarm: number
  onDone(id: number | null): void
  onClosed(): Promise<void>
}) {
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)
  return (
    <form
      /* Latimea e a casetei, nu a panoului: jumatatea de rand iese 330px, cat
         avea si caseta scrisa de mana pana acum (345px). Fara ea, un rand de
         formular s-ar intinde peste tot panoul pentru un singur camp. */
      style={{ display: 'block', padding: 15, verticalAlign: 'top', float: 'left', width: 720, maxWidth: '100%' }}
      method="post"
      onSubmit={(e) => {
        e.preventDefault()
        if (busy) return
        setBusy(true)
        void closeSystemAlarm(id, alarm, description)
          .then(() => {
            onDone(null)
            return onClosed()
          })
          .catch(() => toast('The alarm could not be closed.', 'Error', 'error'))
          .finally(() => setBusy(false))
      }}
    >
      <FormRow>
        <HalfField label="Description" htmlFor={`sa-close-note-${alarm}`}>
          <textarea
            id={`sa-close-note-${alarm}`}
            rows={4}
            name="description"
            className="form-control"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          ></textarea>
        </HalfField>
      </FormRow>
      <input type="hidden" name="alarm" value={alarm} />
      <FormActions>
        <button type="submit" className="btn btn-default" disabled={busy}>
          Submit
        </button>
      </FormActions>
    </form>
  )
}

function HistoryList({
  all,
  shown,
}: {
  all: DeviceSystemAlarmsPage['alarm_history']
  shown: DeviceSystemAlarmsPage['alarm_history']
}) {
  if (all.length === 0) {
    return (
      <div className="sa-hist-list">
        <p className="color-light-grey" style={{ fontSize: 12 }}>
          <i className="fas fa-info-circle"></i> Nothing has been closed on this machine yet.
        </p>
      </div>
    )
  }
  const ids = new Set(shown)
  return (
    <div className="sa-hist-list">
      <table className="table table-hover sa-history" id="sa-history">
        <tbody>
          {all.map((past, i) => {
            const dot = HIST_TONE[past.severity] || '#5b9bd5'
            const hidden = !ids.has(past)
            const byPerson = !!(past.closed_by_name && past.closed_by_name.trim() !== '')
            return (
              <tr
                key={`${past.type_name}-${i}`}
                className={hidden ? 'is-hidden' : undefined}
                data-sev={past.severity}
                data-closed={past.closed_unix || 0}
                data-by={byPerson ? 'person' : 'agent'}
              >
                <td className="sa-dot">
                  <span style={{ background: dot }}></span>
                </td>
                <td>
                  <strong>{past.type_name}</strong>
                  {past.hardware_identifier ? <span className="color-light-grey"> {past.hardware_identifier}</span> : null}
                  {past.note ? <div className="sa-note">{past.note}</div> : null}
                  {past.value_string ? <div className="sa-note">{past.value_string}</div> : null}
                </td>
                <td className="sa-when">
                  {past.opened_at}
                  {past.duration > 0 && <div>lasted {lasted(past.duration)}</div>}
                  {past.closed_at && (
                    <div>
                      closed {past.closed_at}
                      {byPerson ? ` by ${past.closed_by_name}` : ' by the agent'}
                    </div>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <p className="sa-muted sa-hist-empty" id="sa-hist-empty" hidden={shown.length > 0} style={{ fontSize: 12, margin: '8px 0' }}>
        Nothing in the history matches these filters.
      </p>
    </div>
  )
}

function Chips({
  filter,
  value,
  onChange,
  items,
}: {
  filter: string
  value: string
  onChange(v: string): void
  items: Array<{ v: string; label: string; dot?: string }>
}) {
  return (
    <div className="sa-hist-chips" data-filter={filter}>
      {items.map((item) => (
        <button key={item.v + item.label} type="button" className={value === item.v ? 'is-on' : undefined} data-v={item.v} onClick={() => onChange(item.v)}>
          {item.dot ? <span className="sa-hist-dot" style={{ background: item.dot }}></span> : null}
          {item.label}
        </button>
      ))}
    </div>
  )
}

function sentence(steps: Array<string | { step?: string }>): string {
  const names = steps
    .map((s) => (typeof s === 'string' ? s : s.step || ''))
    .filter((s) => s !== '')
  if (names.length === 0) return ''
  if (names.length === 1) return names[0]
  const last = names.pop() as string
  return `${names.join(', ')} and ${last}`
}

function silentMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes} min`
  if (minutes < 2880) return `${Math.round(minutes / 60)} h`
  return `${Math.floor(minutes / 1440)} d`
}

function lasted(seconds: number): string {
  if (seconds < 1) return 'less than a second'
  const units: Array<[string, number]> = [
    ['d', 86400],
    ['h', 3600],
    ['m', 60],
    ['s', 1],
  ]
  for (let i = 0; i < units.length; i++) {
    const value = Math.floor(seconds / units[i][1])
    if (value <= 0) continue
    let out = `${value}${units[i][0]}`
    if (units[i + 1]) {
      const rest = Math.floor((seconds - value * units[i][1]) / units[i + 1][1])
      if (rest > 0) out += ` ${rest}${units[i + 1][0]}`
    }
    return out
  }
  return ''
}

const BOARD_CSS = `
.sa-tile { cursor: pointer; }
.sa-tile .dashboard-stat { transition: transform .12s ease, box-shadow .12s ease; position: relative; }
.sa-tile:hover .dashboard-stat { transform: translateY(-2px); box-shadow: 0 4px 14px rgba(0, 0, 0, .12); }
.sa-tile.sa-active .dashboard-stat { box-shadow: inset 0 0 0 3px rgba(255, 255, 255, .85); }
.sa-section-title { font-size: 11px; text-transform: uppercase; letter-spacing: .05em; color: #8a94a0; margin: 22px 0 10px; padding-bottom: 6px; border-bottom: 1px solid #e3e7ec; }
.sa-layer { background: #fff; border: 1px solid #e3e7ec; border-left: 3px solid #36a3d9; border-radius: 3px; padding: 12px 14px; margin-bottom: 16px; font-size: 13px; color: #34495e; }
.sa-layer.off { border-left-color: #b8c2cf; color: #8a94a0; }
.sa-board { width: 100%; }
.sa-board th { font-size: 11px; text-transform: uppercase; letter-spacing: .04em; color: #8a94a0; font-weight: 600; border-bottom: 1px solid #e3e7ec; padding: 8px 10px; }
.sa-board td { vertical-align: top !important; padding: 10px !important; font-size: 13px; }
.sa-board .sa-dot { width: 12px; padding-right: 0 !important; }
.sa-board .sa-dot span { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-top: 5px; }
.sa-board .sa-muted { color: #8a94a0; font-size: 12px; }
.sa-board .sa-culprit { color: #b16131; font-size: 12px; margin-top: 3px; }
.sa-watches { background: #fff; border: 1px solid #e3e7ec; border-radius: 3px; padding: 12px 14px; margin-bottom: 16px; }
.sa-watches h4 { margin: 0 0 6px; font-size: 13px; font-weight: 600; color: #34495e; }
.sa-watches .sa-watch-help { color: #8a94a0; font-size: 12px; margin: 0 0 10px; }
.sa-watch-list { list-style: none; margin: 0 0 12px; padding: 0; }
.sa-watch-list li { display: flex; align-items: center; gap: 10px; padding: 6px 0; border-top: 1px solid #eef1f4; font-size: 13px; }
.sa-watch-list li:first-child { border-top: 0; }
.sa-watch-list .sa-watch-kind { font-size: 10px; text-transform: uppercase; letter-spacing: .04em; color: #8a94a0; min-width: 52px; }
.sa-watch-list button { margin-left: auto; border: 0; background: none; color: #b16131; font-size: 12px; padding: 0; }
.sa-watch-add { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin-top: 6px; }
.sa-watch-add select, .sa-watch-add input[type="text"], .sa-watch-add input[type="number"] { width: auto; max-width: 260px; height: 30px; padding: 4px 8px; font-size: 12px; }
.sa-watch-add button { height: 30px; }
.sa-history { width: 100%; }
.sa-history td { vertical-align: top !important; padding: 10px 12px !important; }
.sa-history .sa-when { white-space: nowrap; color: #8a94a0; font-size: 12px; }
.sa-history .sa-note { color: #8a94a0; font-size: 12px; margin-top: 3px; }
.sa-history .sa-dot { width: 10px; padding-right: 0 !important; }
.sa-history .sa-dot span { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-top: 6px; }
.sa-board:not(.is-now) .sa-toggle { display: none; }
.sa-board.is-now td:has(.sa-toggle) .sa-include-text { display: none; }
.sa-board:not(.is-wide) .sa-col-module { display: none; }
.sa-board.is-now .sa-col-layer, .sa-board.is-now .sa-col-threshold { display: none; }
.sa-board.is-now .sa-watch-else { display: none; }
.sa-board:not(.is-now) .sa-watch-now { display: none; }
.sa-board.is-now .sa-current { font-weight: 600; font-size: 14px; color: #2c3e50; }
.sa-board .sa-module-cell { white-space: nowrap; color: #8a94a0; font-size: 12px; }
.sa-board.is-all .sa-toggle { display: inline-block; }
.sa-board.is-all td:has(.sa-toggle) .sa-include-text { display: none; }
.sa-board.is-all .sa-watch-else { display: none; }
.sa-board.is-all .sa-watch-now { display: inline; }
.sa-tile .sa-chev { position: absolute; right: 12px; bottom: 8px; color: rgba(255, 255, 255, .75); font-size: 12px; transition: transform .15s ease; }
.sa-tile.sa-active .sa-chev { transform: rotate(180deg); color: #fff; }
.sa-pane { margin-top: 4px; }
.sa-split { margin-top: 18px; }
.sa-split.is-wide-now .sa-col-now { width: 100%; }
.sa-split.is-wide-now .sa-col-history { width: 100%; margin-top: 18px; }
.sa-col-history { border-left: 1px solid #e3e7ec; }
.sa-split.is-wide-now .sa-col-history { border-left: 0; }
@media (max-width: 991px) { .sa-col-history { border-left: 0; margin-top: 18px; } }
.sa-hist-count { color: #b8c2cf; font-weight: 400; text-transform: none; letter-spacing: 0; margin-left: 6px; }
.sa-hist-tools { margin: 0 0 10px; }
.sa-hist-tools input[type="search"] { width: 100%; height: 32px; border: 1px solid #e3e7ec; border-radius: 3px; padding: 0 10px; font-size: 13px; margin-bottom: 8px; }
.sa-hist-tools input[type="search"]:focus { outline: 0; border-color: #36a3d9; }
.sa-hist-chips { display: flex; flex-wrap: wrap; gap: 4px 6px; margin-bottom: 6px; }
.sa-hist-chips button { border: 1px solid #e3e7ec; background: #fff; color: #6b7480; font-size: 11px; padding: 2px 9px; border-radius: 12px; line-height: 18px; }
.sa-hist-chips button.is-on { border-color: #36a3d9; color: #36a3d9; background: rgba(54, 163, 217, .06); }
.sa-hist-dot { display: inline-block; width: 7px; height: 7px; border-radius: 50%; margin-right: 5px; vertical-align: middle; }
.sa-hist-list { max-height: 720px; overflow-y: auto; }
.sa-history tr.is-hidden { display: none; }
.sa-muted { color: #8a94a0; font-size: 12px; }
`
